<template>
  <div class="pb-calendar">
    <div class="pb-main-calendar">
      <full-calendar v-on:dblclick.native="onCalDoubleClick" ref="calendar" :events="loadAppointments" @event-selected="onSelectClicked" :config="config"></full-calendar>
      <Notifications v-show="showNotifications" />
      <Settings v-show="showSettings" />
      <div v-show="notifications.length > 0" class="pb-notification__signal"></div>
    </div>

    <MiniCalendar v-show="showMiniCalendar" />

    <Appointment v-if="appointment" :appointment="appointment" />
    <Booking v-if="showBookingModal" />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import { ADD_TOAST_MESSAGE } from 'vuex-toast';
import moment from 'moment-timezone';
import Appointment from '@/components/Appointment';
import Settings from '@/components/Settings';
import Notifications from '@/components/Notifications';
import Booking from '@/components/Booking';
import MiniCalendar from '@/components/MiniCalendar';
import { getDayFromCalendarDate } from '@/utils';

export default {
  name: 'Calendar',
  mounted() {
    this.selectDay(this.$refs.calendar.fireMethod('getDate'));
    this.setupClickListener();
    this.pollNotifications();
  },
  destroyed() {
    this.cancelPollNotifications();
  },
  data() {
    return {
      config: {
        timezone: this.currentTimezone(),
        timeFormat: 'h:mm',
        slotLabelFormat: 'h:mm A',
        allDaySlot: false,
        slotDuration: '00:15:00',
        contentHeight: window.innerHeight - 64,
        disableDragging: true,
        disableResizing: true,
        selectable: false,
        editable: false,
        eventClick: this.onSelectClicked,
        viewRender: () => {
          this.enableToday();
        },
        dayClick: (date) => {
          this.selectDay(moment.utc(date));
        },
        eventTextColor: '#010080',
        columnHeaderHtml: calMoment => `<span class="pb-day-header-copy">${calMoment.format('dddd')}<br /><span class='pb-header-numbers'>${calMoment.format('M/D')}</span></span>`,
        header: {
          left: 'cal, prev, next, title, today',
          center: '',
          right: 'book, notifications, settings',
        },
        buttonIcons: {
          prev: 'otto-icon-prev',
          next: 'otto-icon-next',
        },
        customButtons: {
          today: {
            text: 'TODAY',
            click: () => {
              this.$refs.calendar.fireMethod('today');
              this.selectDay(this.$refs.calendar.fireMethod('getDate'));
              this.enableToday();
            },
          },
          book: {
            text: 'BOOK APPOINTMENT',
            click: () => {
              this.onBookAppointmentClick();
            },
          },
          notifications: {
            text: 'Note',
            icon: 'otto otto-icon otto-icon-notification',
            click: () => {
              this.toggleNotifications();
            },
          },
          settings: {
            text: '',
            icon: 'otto otto-icon otto-icon-settings',
            click: () => {
              this.toggleSettings();
            },
          },
          cal: {
            text: '',
            icon: 'otto otto-icon otto-icon-cal',
            click: () => {
              this.toggleMiniCalendar();
            },
          },
        },
      },
      selected: null,
    };
  },
  methods: {
    ...mapGetters([
      'currentTimezone',
    ]),
    ...mapActions([
      'getCurrentAppointments',
      'selectAppointment',
      'toggleBooking',
      'setCurrentDay',
      'setCurrentCalendarDay',
      'showBooking',
      'pollNotifications',
      'cancelPollNotifications',
      'closeNotifications',
      'toggleNotifications',
      'toggleMiniCalendar',
      'toggleSettings',
      'bookSelectedDate',
    ]),
    ...mapActions({
      addToast: ADD_TOAST_MESSAGE,
    }),
    refreshEvents() {
      this.$refs.calendar.$emit('refetch-events');
    },
    removeEvent() {
      this.$refs.calendar.$emit('remove-event', this.selected);
      this.selected = {};
    },
    onSelectClicked(event) {
      this.selectAppointment(event);
    },
    loadAppointments(startDate, endDate, timezone, callback) {
      this.getCurrentAppointments({ startDate, endDate })
        .then((currentApps) => {
          callback(currentApps);
        })
        .catch(() => {
          this.$store.dispatch('logout')
            .then(() => {
              this.$router.replace('login');
              this.addToast({
                text: 'Your session has expired. Please login again.',
                type: 'danger',
              });
            });
        });
    },
    removeHighlightedDate() {
      const highightedItems = document.querySelectorAll('.otto-calendar-date--selected');
      Array.prototype.forEach.call(highightedItems, item => item.classList.remove('otto-calendar-date--selected'));
    },
    highlightDate(date) {
      this.removeHighlightedDate();
      const newDate = moment(date);
      const newSelector = `[data-date="${newDate.format('YYYY-MM-DD')}"]`;
      const itemsToHighlight = document.querySelectorAll(newSelector);

      Array.prototype.forEach.call(itemsToHighlight, item => item.classList.add('otto-calendar-date--selected'));
    },
    selectDay(date) {
      const day = getDayFromCalendarDate(date);
      this.setCurrentCalendarDay(day);
    },
    calHeaderClicked(headerItem) {
      const { date } = headerItem.dataset;
      this.setCurrentCalendarDay(`${date}T00:00:00.000`);
    },
    setupClickListener() {
      this.$el.querySelector('.pb-main-calendar').addEventListener('click', (event) => {
        const headerItem = event.target.classList.contains('fc-day-header') ? event.target : event.target.closest('.fc-day-header');
        if (!headerItem || headerItem.classList.contains('otto-calendar-date--selected')) {
          return false;
        }

        this.calHeaderClicked(headerItem);
        return true;
      });
    },
    enableToday() {
      const todayBtn = this.$el.querySelector('.fc-today-button');
      todayBtn.classList.remove('fc-state-disabled');
      todayBtn.removeAttribute('disabled');
    },
    onCalDoubleClick(e) {
      if (e.target.classList.contains('fc-widget-content')) {
        this.bookSelectedDate();
      }
    },
    onBookAppointmentClick() {
      this.closeNotifications();
      this.toggleBooking();
    },
  },
  computed: {
    ...mapState({
      showBookingModal: state => state.ui.showBookingModal,
      appointment: state => state.appointment.selectedAppointment,
      currentAppointments: state => state.appointment.currentAppointments,
      currentCalendarDay: state => state.appointment.currentCalendarDay,
      notifications: state => state.notifications.list,
      showNotifications: state => state.ui.showNotifications,
      showSettings: state => state.ui.showSettings,
      showMiniCalendar: state => state.ui.showMiniCalendar,
    }),
  },
  watch: {
    currentAppointments: {
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.refreshEvents();
        }
      },
      deep: true,
    },
    currentCalendarDay: {
      handler(newValue) {
        if (newValue !== null) {
          this.$refs.calendar.fireMethod('gotoDate', newValue);
          this.highlightDate(moment.parseZone(newValue));
        }
      },
    },
  },
  components: {
    Appointment,
    Settings,
    Booking,
    Notifications,
    MiniCalendar,
  },
};
</script>
<style lang="sass" src="@/assets/styles/calendar.sass"></style>




// WEBPACK FOOTER //
// src/components/Calendar.vue